import { useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { BestOfPhoto } from '../game/types';
import { assetUrl } from '../game/logic';
import { recupererBestOfEnLigne } from '../game/api';

interface Props {
  photos: BestOfPhoto[];
  onFermer: () => void;
}

const srcPhoto = (src: string) => (src.startsWith('http') ? src : assetUrl(src));

export const BestOf = ({ photos, onFermer }: Props) => {
  const [index, setIndex] = useState<number | null>(null);
  const [enLigne, setEnLigne] = useState<BestOfPhoto[]>([]);

  useEffect(() => {
    recupererBestOfEnLigne().then((liste) =>
      setEnLigne(liste.map((p) => ({ src: p.url, legende: p.legende })))
    );
  }, []);

  const toutes = [...photos, ...enLigne];

  if (index !== null) {
    const photo = toutes[index];
    return (
      <div className="fixed inset-0 z-50 bg-white flex flex-col anim-apparition" data-testid="bestof-visionneuse">
        <div className="flex items-center justify-between px-4 h-14 shrink-0">
          <span className="text-sm text-encre-clair tabular-nums">{index + 1} / {toutes.length}</span>
          <button
            data-testid="bestof-fermer-photo-btn"
            onClick={() => setIndex(null)}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-encre-clair"
            aria-label="Retour à la grille"
          >
            <X size={22} />
          </button>
        </div>
        <div className="flex-1 flex items-center justify-center px-4 min-h-0">
          <img src={srcPhoto(photo.src)} alt={photo.legende} className="max-h-full max-w-full object-contain border border-encre" />
        </div>
        <div className="shrink-0 px-6 py-4 pb-8 text-center">
          <p className="font-script text-3xl text-encre leading-tight">{photo.legende}</p>
          <div className="flex justify-center gap-4 mt-4">
            <button
              data-testid="bestof-precedente-btn"
              onClick={() => setIndex((index - 1 + toutes.length) % toutes.length)}
              className="min-h-[48px] min-w-[64px] rounded-full border border-encre text-encre flex items-center justify-center active:bg-brume"
              aria-label="Photo précédente"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              data-testid="bestof-suivante-btn"
              onClick={() => setIndex((index + 1) % toutes.length)}
              className="min-h-[48px] min-w-[64px] rounded-full border border-encre text-encre flex items-center justify-center active:bg-brume"
              aria-label="Photo suivante"
            >
              <ChevronRight size={22} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-y-auto anim-apparition" data-testid="bestof-galerie">
      <div className="max-w-md mx-auto px-6 py-6 pb-12">
        <button
          data-testid="bestof-fermer-btn"
          onClick={onFermer}
          className="min-h-[44px] flex items-center gap-2 text-encre-clair font-medium"
        >
          <X size={20} /> Fermer
        </button>
        <h2 className="font-script text-encre text-5xl mt-4">Le best of</h2>
        <p className="petit-cap mt-2">Les figures de la quête, en majesté</p>
        <div className="grid grid-cols-2 gap-4 mt-6">
          {toutes.map((p, i) => (
            <button
              key={p.src}
              data-testid={`bestof-photo-${i}`}
              onClick={() => setIndex(i)}
              className="text-left border border-encre overflow-hidden transition-transform duration-200 active:scale-[0.97]"
            >
              <img src={srcPhoto(p.src)} alt={p.legende} loading="lazy" className="w-full aspect-[3/4] object-cover" />
              <p className="text-xs font-medium text-encre px-3 py-2 leading-tight">{p.legende}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
